import { baseUrl } from '../../utils/baseUrl';
import { isLayoutUuid } from './layoutLinkUtils';

const HTTP_URL_RE = /^https?:\/\//i;
const DATA_IMAGE_RE = /^data:image\//i;
const MONGO_ID_RE = /^[0-9a-fA-F]{24}$/;

/** Values that must never be passed to `<img src>` — they 404 as relative paths. */
export const isInvalidFloorPlanPreviewToken = (value) => {
  if (value == null) return true;
  const s = String(value).trim();
  if (!s || s === 'undefined' || s === 'null') return true;
  if (MONGO_ID_RE.test(s)) return true;
  if (isLayoutUuid(s)) return true;
  return false;
};

/**
 * Resolve a saved-layout image field to a URL suitable for `<img src>`.
 * Returns null when the token is empty, a Mongo id, or otherwise unusable.
 */
export function resolveFloorPlanPreviewUrl(raw) {
  if (isInvalidFloorPlanPreviewToken(raw)) return null;
  const value = String(raw).trim();
  if (HTTP_URL_RE.test(value) || value.startsWith('blob:') || DATA_IMAGE_RE.test(value)) {
    return value;
  }
  const base = String(baseUrl || '').replace(/\/$/, '');
  if (!base) return null;
  if (value.startsWith('/g/') || value.startsWith('g/')) {
    const path = value.startsWith('/') ? value.slice(1) : value;
    return `${base}/${path}`;
  }
  if (value.startsWith('/')) {
    return `${base}${value}`;
  }
  return `${base}/g${value}`;
}

const PREVIEW_FIELD_GETTERS = [
  (plan) => plan?.furnishedThumbnail,
  (plan) => plan?.thumbnail,
  (plan) => plan?.blankImage,
];

/** Ordered, deduped preview URLs for a normalized plan row. */
export function buildFloorPlanPreviewCandidates(plan) {
  if (!plan) return [];
  const out = [];
  const seen = new Set();
  PREVIEW_FIELD_GETTERS.forEach((pick) => {
    const resolved = resolveFloorPlanPreviewUrl(pick(plan));
    if (resolved && !seen.has(resolved)) {
      seen.add(resolved);
      out.push(resolved);
    }
  });
  return out;
}

const RAW_PREVIEW_FIELDS = [
  'thumbnailImage',
  'thumbnailfurnishedImageId',
  'furnishedImageId',
  'image',
  'furnishedThumbnail',
  'thumbnail',
  'blankImage',
];

/** Prefer whichever catalog row has loadable preview URLs. */
export function mergeLayoutPreviewFields(primary, fallback) {
  if (!primary) return fallback || null;
  if (!fallback) return primary;
  const primaryNorm = normalizePreviewFieldsOnRow(primary);
  if (buildFloorPlanPreviewCandidates(primaryNorm).length > 0) {
    return primary;
  }
  const merged = { ...primary };
  RAW_PREVIEW_FIELDS.forEach((field) => {
    const fallbackVal = fallback[field];
    if (fallbackVal == null || fallbackVal === '') return;
    const currentVal = merged[field];
    if (
      currentVal == null ||
      currentVal === '' ||
      isInvalidFloorPlanPreviewToken(currentVal)
    ) {
      merged[field] = fallbackVal;
    }
  });
  return merged;
}

/** Map getByPartner / enriched-link field names onto normalized preview keys. */
export function normalizePreviewFieldsOnRow(row) {
  if (!row) return null;
  return {
    ...row,
    furnishedThumbnail:
      row.furnishedThumbnail ||
      row.thumbnailfurnishedImageId ||
      row.furnishedImageId ||
      null,
    thumbnail:
      row.thumbnail ||
      row.thumbnailImage ||
      row.image ||
      null,
    blankImage: row.blankImage || row.image || row.thumbnailImage || null,
  };
}

export function withResolvedPlanPreviewFields(normalizedPlan) {
  if (!normalizedPlan) return null;
  const furnished = resolveFloorPlanPreviewUrl(
    normalizedPlan.furnishedThumbnail ||
      normalizedPlan.thumbnailfurnishedImageId ||
      normalizedPlan.furnishedImageId
  );
  const thumbnail = resolveFloorPlanPreviewUrl(
    normalizedPlan.thumbnail || normalizedPlan.thumbnailImage || normalizedPlan.image
  );
  const blank = resolveFloorPlanPreviewUrl(
    normalizedPlan.blankImage || normalizedPlan.image || normalizedPlan.thumbnailImage
  );
  return {
    ...normalizedPlan,
    furnishedThumbnail: furnished,
    thumbnail: thumbnail || blank,
    blankImage: blank || thumbnail,
  };
}
